package com.dignicate.web.viewmodels

import com.dignicate.web.domain.AboutContent
import com.dignicate.web.domain.CompanyContent
import com.dignicate.web.domain.ContentRepository
import com.dignicate.web.domain.FooterNav
import com.dignicate.web.domain.GetCompanyContentUseCase
import com.dignicate.web.domain.GetFooterNavUseCase
import com.dignicate.web.domain.GetMainPageContentUseCase
import com.dignicate.web.domain.GetNavigationLabelsUseCase
import com.dignicate.web.domain.HeroContent
import com.dignicate.web.domain.Language
import com.dignicate.web.domain.MainPageContent
import com.dignicate.web.domain.NavigationLabels
import com.dignicate.web.domain.ProductInfo
import com.dignicate.web.domain.Resource
import com.dignicate.web.domain.TechnologyArea
import com.dignicate.web.domain.ValueStatement
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.launchIn
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.launch

/**
 * メイン画面全体の UI 状態
 * 各セクションのコンテンツを保持
 */
data class MainUiState(
    val hero: HeroContent? = null,
    val about: AboutContent? = null,
    val technologies: List<TechnologyArea>? = null,
    val products: List<ProductInfo>? = null,
    val values: List<ValueStatement>? = null,
    val navigationLabels: NavigationLabels? = null,
    val footerNav: FooterNav? = null,

    // Section titles and descriptions provided by repository
    val technologiesTitle: String = "",
    val technologiesDescription: String = "",
    val productsTitle: String = "",
    val productsDescription: String = "",
    val valuesTitle: String = "",
    val valuesDescription: String = "",

    val company: CompanyContent? = null,

    val isLoading: Boolean = false,
    val error: String? = null
)

class MainViewModel(
    private val getMainPageContentUseCase: GetMainPageContentUseCase,
    private val getNavigationLabelsUseCase: GetNavigationLabelsUseCase,
    private val getCompanyContentUseCase: GetCompanyContentUseCase,
    private val getFooterNavUseCase: GetFooterNavUseCase
) : ViewModel() {

    private val _uiState = MutableStateFlow(MainUiState())
    val uiState: StateFlow<MainUiState> = _uiState.asStateFlow()

    init {
        setupBindings()
    }

    private fun setupBindings() {
        getMainPageContentUseCase.data.onEach { resource ->
            _uiState.value = when (resource) {
                is Resource.Initialized -> {
                    MainUiState()
                }
                is Resource.InProgress -> {
                    _uiState.value.copy(isLoading = true)
                }
                is Resource.Success<MainPageContent> -> {
                    _uiState.value.copy(
                        hero = resource.data.hero,
                        about = resource.data.about,
                        technologies = resource.data.technologies,
                        products = resource.data.products,
                        values = resource.data.values,
                        technologiesTitle = resource.data.technologiesTitle,
                        technologiesDescription = resource.data.technologiesDescription,
                        productsTitle = resource.data.productsTitle,
                        productsDescription = resource.data.productsDescription,
                        valuesTitle = resource.data.valuesTitle,
                        valuesDescription = resource.data.valuesDescription,
                        isLoading = false,
                        error = null
                    )
                }
                is Resource.Failure -> {
                    _uiState.value.copy(
                        isLoading = false,
                        error = resource.error.message
                    )
                }
            }
        }.launchIn(viewModelScope)

        getNavigationLabelsUseCase.data.onEach { resource ->
            if (resource is Resource.Success<NavigationLabels>) {
                _uiState.value = _uiState.value.copy(navigationLabels = resource.data)
            }
        }.launchIn(viewModelScope)

        getCompanyContentUseCase.data.onEach { resource ->
            when (resource) {
                is Resource.Success<CompanyContent> -> {
                    _uiState.value = _uiState.value.copy(company = resource.data)
                }
                is Resource.Failure<*>, Resource.InProgress, Resource.Initialized -> {
                    // no-op
                }
            }
        }.launchIn(viewModelScope)

        getFooterNavUseCase.data.onEach { resource ->
            if (resource is Resource.Success) {
                _uiState.value = _uiState.value.copy(footerNav = resource.data)
            }
        }.launchIn(viewModelScope)
    }

    override fun onCreated() {
        viewModelScope.launch {
            getMainPageContentUseCase.fetch()
            getNavigationLabelsUseCase.fetch()
            getCompanyContentUseCase.fetch()
            getFooterNavUseCase.fetch()
        }
    }
}
